<template>
    <div>
        <!-- DIALOG -->
        <el-dialog :title="`${actionButtonText} ${title}`" width="500" v-model="dialog" close-on-press-escape draggable>
            <el-form
                ref="formInstance"
                :model="formData"
                :rules="rules"
                :disabled="isPosting || isPutting"
                label-position="top"
                size="large"
                status-icon
                @submit.prevent="save"
            >
                <!-- FORM SLOT -->
                <slot name="form"></slot>
            </el-form>

            <template #footer>
                <el-button
                    @click="save"
                    :loading="isPosting || isPutting"
                    :disabled="isPosting || isPutting"
                    type="success"
                >
                    {{ actionButtonText }}
                </el-button>
            </template>
        </el-dialog>

        <!-- SEARCH & ADD -->
        <div class="d-flex justify-between gap-2 mb-4">
            <el-input v-model="search" :placeholder="$t('Search')" :prefix-icon="Search" size="large" clearable />
            <el-button 
                v-if="canReorder" 
                size="large" 
                :type="isReordering ? 'primary' : 'default'" 
                @click="toggleReorder"
            >
                <i class="fas fa-sort pr-2"></i> {{ isReordering ? 'Done' : 'Reorder' }}
            </el-button>
        </div>

        <!-- TABLE / REORDER VIEW -->
        <el-skeleton v-if="isFetching" :rows="5" animated />
        <template v-else>
            <div v-if="isReordering" class="reorder-container border rounded-xl pa-4">
                <Reorder.Group v-model:values="data" class="reorder-group" axis="y">
                    <Reorder.Item 
                        v-for="item in data" 
                        :key="item[primaryKey]" 
                        :value="item" 
                        class="reorder-item mb-2 pa-3 bg-white border rounded shadow-sm d-flex align-center cursor-move"
                    >
                        <i class="fas fa-grip-vertical pr-4 text-grey"></i>
                        <span class="text-truncate">{{ item.questionText || item.title || item.name || 'Item ' + item[primaryKey] }}</span>
                    </Reorder.Item>
                </Reorder.Group>
            </div>
            <EasyDataTable
                v-else
                border-cell
                multi-sort
                show-index
                hide-footer
                header-text-direction="center"
                body-text-direction="center"
                table-class-name="customize-easy-table"
                :headers="headers"
                :items="paginatedData"
                :sortType="['desc', 'asc']"
                :rows-per-page="1000"
                :table-height="500"
            >
                <template #item-action="item">
                    <div class="d-flex align-center justify-center pa-2">
                        <el-button type="warning" @click="openDialog(item[primaryKey], item)" :icon="Edit" circle plain />
                        <el-popconfirm title="Are you sure to delete?" @confirm="remove(item[primaryKey])">
                            <template #reference>
                                <el-button type="danger" :icon="Delete" circle plain />
                            </template>
                        </el-popconfirm>
                    </div>
                </template>
            </EasyDataTable>
        </template>

        <!-- PAGINATION -->
        <el-scrollbar>
            <div class="pa-2 border rounded-b-xl d-flex align-center">
                <el-pagination
                    v-if="filteredData.length"
                    :total="filteredData.length"
                    :page-size="pageSize"
                    :current-page="page"
                    background
                    layout="prev, pager, next"
                    @current-change="handlePageChange"
                />
                <el-select class="mx-2 my-2" v-model="pageSize" placeholder="Select" style="width: 70px">
                    <el-option :label="10" :value="10" />
                    <el-option :label="50" :value="50" />
                    <el-option :label="100" :value="100" />
                </el-select>
                <div class="mx-2 mt-2">{{ $t('Total') }}: {{ filteredData.length }}</div>
            </div>
        </el-scrollbar>
    </div>
</template>

<script lang="ts" setup>
    import { ref, computed, onMounted } from 'vue'
    import axios from '@/utils/apiClient'
    import { Search, Edit, Delete } from '@element-plus/icons-vue'
    import type { FormRules } from 'element-plus'
    import type { Header } from 'vue3-easy-data-table'
    import { Reorder } from 'motion-v'
    import { ElNotification } from 'element-plus'

    const props = defineProps({
        endpoint: { type: String, required: true },
        formData: { type: Object, required: true },
        headers: { type: Array as () => Header[], required: true },
        rules: { type: Object as () => FormRules, required: true },
        primaryKey: { type: String, default: 'id' },
        title: { type: String, default: '' },
        canReorder: { type: Boolean, default: false },
    })

    /* state */
    const data = ref<any[]>([])
    const search = ref('')
    const page = ref(1)
    const pageSize = ref(10)
    const isFetching = ref(false)
    const isPosting = ref(false)
    const isPutting = ref(false)
    const isReordering = ref(false)
    const dialog = ref(false)
    const actionButtonText = ref('Save')

    const formInstance = ref()

    /* computed: search filter */
    const filteredData = computed(() => {
        if (!search.value) return data.value
        const keyword = search.value.toLowerCase()
        return data.value.filter(item =>
            Object.values(item).some(v => v !== null && v !== undefined && String(v).toLowerCase().includes(keyword)),
        )
    })

    /* computed: pagination */
    const paginatedData = computed(() => {
        const start = (page.value - 1) * pageSize.value
        return filteredData.value.slice(start, start + pageSize.value)
    })

    /* methods */
    const fetchData = async () => {
        try {
            isFetching.value = true
            const res = await axios.get(props.endpoint)
            data.value = Array.isArray(res.data) ? res.data : (res.data?.data ?? [])
        } finally {
            isFetching.value = false
        }
    }

    const toggleReorder = () => {
        isReordering.value = !isReordering.value
        if (!isReordering.value) {
            saveReorder()
        }
    }

    const saveReorder = async () => {
        try {
            isFetching.value = true
            const ids = data.value.map(item => item[props.primaryKey])
            await axios.post(`${props.endpoint}/Reorder`, ids)
            ElNotification.success({ title: 'Success', message: 'Order updated successfully' })
            await fetchData()
        } catch (error) {
            console.error('Reorder error:', error)
            ElNotification.error({ title: 'Error', message: 'Failed to update order' })
            await fetchData()
        } finally {
            isFetching.value = false
        }
    }

    const openDialog = (id = 0, item: any = null) => {
        if (item) {
            Object.assign(props.formData, item)
            actionButtonText.value = 'Update'
        } else {
            for (const key in props.formData) props.formData[key] = null
            actionButtonText.value = 'Save'
        }
        dialog.value = true
    }

    const save = async () => {
        try {
            if (props.formData[props.primaryKey]) {
                isPutting.value = true
                await axios.put(`${props.endpoint}/${props.formData[props.primaryKey]}`, props.formData)
            } else {
                isPosting.value = true
                await axios.post(props.endpoint, props.formData)
            }
            await fetchData()
            dialog.value = false
        } finally {
            isPosting.value = false
            isPutting.value = false
        }
    }

    const remove = async (id: number) => {
        await axios.delete(`${props.endpoint}/${id}`)
        await fetchData()
    }

    const handlePageChange = (newPage: number) => {
        page.value = newPage
    }

    /* init */
    onMounted(fetchData)
</script>

<style scoped>
.reorder-container {
    background-color: #f9f9f9;
    min-height: 200px;
}
.reorder-item {
    transition: box-shadow 0.2s;
    user-select: none;
}
.reorder-item:active {
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    z-index: 100;
}
.cursor-move {
    cursor: move;
}
.text-grey {
    color: #909399;
}
</style>
